import { AGENT_CONFIG_MAP } from "~/constants/agents/configs";
import EXECUTION_MODE_DISPLAY from "~/constants/agents/executionMode";
import { DEFAULT_LOCAL_AGENT, LOCAL_AGENT_KEY } from "~/constants/localAgent";
import { USER_KIND_CUSTOM, USER_KIND_DART_AI, USER_KIND_USER } from "~/constants/userKinds";
import { isDefined } from "~/shared/common";
import { AiModel, AiThinkingLevel, ExecutionMode, ReassignBehavior, UserStatus } from "~/shared/enums";
import type { AgentApiWebhook, AgentExtension, User } from "~/shared/types";
import { getText, isString, makeEmptyLexicalState } from "~/utils/common";
import { validateIsNotEmpty } from "~/utils/validation";

export const AGENT_POST_BODY_EXAMPLE = `{
  "data": {{data}}
}`;

export const DEFAULT_AGENT_API_WEBHOOK: AgentApiWebhook = {
  url: "",
  headers: [],
  body: AGENT_POST_BODY_EXAMPLE,
  responseKey: "",
};

export const isAgentUser = (user: User) => user.kind !== USER_KIND_USER;
export const isNativeAgent = (agent: User) => agent.kind === USER_KIND_CUSTOM;
export const isDartAiAgent = (agent: User) => agent.kind === USER_KIND_DART_AI;
export const isIntegratedAgent = (agent: User) => isAgentUser(agent) && !isNativeAgent(agent) && !isDartAiAgent(agent);
export const isInaccessibleAgent = (agent: User) => isAgentUser(agent) && agent.agent === null;
export const canAgentUseDartAiLayer = (agent: User) => isAgentUser(agent) && !isDartAiAgent(agent);

export const isAgentDisabled = (agent: User) => agent.status === UserStatus.AGENT_DISABLED;
export const isAgentConfigError = (agent: User) => agent.status === UserStatus.AGENT_CONFIG_ERROR;
export const isAgentInactive = (agent: User) => isAgentDisabled(agent) || isAgentConfigError(agent);

export const canAgentUseMemory = (agent: User): boolean =>
  isDartAiAgent(agent) ||
  (isNativeAgent(agent) && agent.agent?.executionMode === ExecutionMode.PROMPT) ||
  (canAgentUseDartAiLayer(agent) && (agent.agent?.dartAiLayerEnabled ?? false));

export const getUserInitials = (user: User): string => {
  const name = user.name
    .replace(/[^\p{L}\p{N}\s]/gu, "")
    .trim()
    .replace(/\s+/g, " ");
  if (name.length === 0) {
    if (isAgentUser(user)) {
      return "A";
    }
    return user.email[0]?.toUpperCase() ?? "U";
  }

  const parts = name.split(" ");
  if (parts.length === 1) {
    return parts[0][0].toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const agentIsUnconfiguredTemplate = (agent: User) =>
  isAgentInactive(agent) &&
  agent.kind !== USER_KIND_CUSTOM &&
  agent.kind !== USER_KIND_DART_AI &&
  !(AGENT_CONFIG_MAP.get(agent.kind)?.configFields ?? [])
    .filter((e) => (e.type === "text" || e.type === "password") && !e.optional)
    .some((e) => isDefined(agent.agent?.config?.[e.key]));

export const isAgentSetupRequired = (agent: User): boolean => {
  if (agent.kind === USER_KIND_DART_AI) {
    return false;
  }
  if (agent.kind === USER_KIND_CUSTOM) {
    if (agent.agent?.executionMode === ExecutionMode.PROMPT) {
      return !agent.agent.prompt || getText(agent.agent.prompt).trim() === "";
    }
    if (agent.agent?.executionMode === ExecutionMode.API) {
      return (agent.agent.config.apiWebhook?.url ?? "").trim() === "";
    }
    if (agent.agent?.executionMode === ExecutionMode.LOCAL) {
      return !agent.agent.localOnline || agent.agent.localState?.loggedIn === false;
    }
    return false;
  }
  return (AGENT_CONFIG_MAP.get(agent.kind)?.configFields ?? []).some((field) => {
    if (field.type !== "text" && field.type !== "password") {
      return false;
    }
    const configValue = agent.agent?.config?.[field.key];
    const value = isString(configValue) ? configValue.trim() : "";
    if (field.optional && value === "") {
      return false;
    }
    return !(field.validate ?? validateIsNotEmpty)(value).isValid;
  });
};

export const getUserDisplayName = (user: User): string => {
  if (isAgentUser(user)) {
    return user.name.trim();
  }
  return user.name.trim() || user.email;
};

export const getActiveDescriptionText = (user: User): string =>
  (user.usingGeneratedDescription ? user.generatedDescription : user.manualDescription)?.trim() ?? "";

export const splitCurrentUserItem = <T>(
  items: T[],
  currentUserDuid: string,
  getUserDuid: (item: T) => string | null | undefined,
  currentUserItemOverride?: T
): { currentUserItem: T | undefined; otherItems: T[] } => ({
  currentUserItem: currentUserItemOverride ?? items.find((item) => getUserDuid(item) === currentUserDuid),
  otherItems: items.filter((item) => getUserDuid(item) !== currentUserDuid),
});

export const getAgentDescriptionText = (user: User): string => {
  const activeDescription = getActiveDescriptionText(user);
  const configDescription = AGENT_CONFIG_MAP.get(user.kind)?.description;
  const { agent } = user;
  const mode = agent?.executionMode;
  const instructionsText = agent && mode === ExecutionMode.PROMPT ? getText(agent.prompt).trim() : "";
  return (
    activeDescription || configDescription || instructionsText || (mode ? EXECUTION_MODE_DISPLAY[mode].description : "")
  );
};

export const getUserDescriptionText = (user: User): string => {
  const activeDescription = getActiveDescriptionText(user);
  if (activeDescription) {
    return activeDescription;
  }

  if (isAgentUser(user)) {
    return getAgentDescriptionText(user);
  }

  return "";
};

export const makeDefaultAgentExtension = (overrides: Partial<AgentExtension> = {}): AgentExtension => {
  const config =
    overrides.executionMode === ExecutionMode.LOCAL
      ? { [LOCAL_AGENT_KEY]: DEFAULT_LOCAL_AGENT, ...overrides.config }
      : (overrides.config ?? {});

  return {
    defaultKind: null,
    executionMode: ExecutionMode.PROMPT,
    localOnline: false,
    localState: null,
    prompt: makeEmptyLexicalState(),
    model: AiModel.AUTO,
    thinkingLevel: AiThinkingLevel.LOW,
    webEnabled: true,
    memoryEnabled: true,
    allowAllMcps: true,
    mcpAllowlistDuids: [],
    allowAllTools: true,
    toolAllowlist: [],
    dartAiLayerEnabled: false,
    webhookDuid: null,
    reassignBehavior: ReassignBehavior.ASSIGNER,
    reassignUserDuid: null,
    assignStatusDuid: null,
    completeStatusDuid: null,
    failStatusDuid: null,
    activeSessions: [],
    accessibleByTeam: true,
    accessibleByUserDuids: [],
    ...overrides,
    config,
  };
};
